import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

import './vessel-edit-schedule-add';
import './vessel-edit-schedule-modify';
import './vessel-edit-schedule-warning';

import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { deepCopy, GetFormattedDate, ConvertDateToAnotherTimeZone, ONE_MONTH } from '../../../src/utils/helpers';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { ScheduleEntry, SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';

@customElement("vessel-edit-schedule")
export class VesselEditSchedule extends LitElement {

    @property({attribute: false}) _schedule: ScheduleEntry[] = [];

    constructor() {
        super();

        this._add = this._add.bind(this);
        this._omit = this._omit.bind(this);
        this._modify = this._modify.bind(this);
        this._saveAndClose = this._saveAndClose.bind(this);
        this._fetchSchedule = this._fetchSchedule.bind(this);
    }

    static get styles() {
        return [
            css`
                .container {
                    display: grid;
                    grid-template-rows: repeat(10, auto);
                    place-content: center;
                    width: 350px;
                    border: 1px solid var(--neutrals-grey-07);
                    background: var(--neutrals-grey-10);
                    font-family: 'MaerskText';
                    font-size: 14px;
                }

                .header {
                    font-size: 10px;
                    color: var(--neutrals-grey-04);;
                    padding: 15px;
                }

                .ports {
                    max-height: calc(100vh - 300px);
                    overflow-y: scroll;
                    margin: 10px 0px 10px 10px;
                }

                .port {
                    display: grid;
                    grid-template-rows: 1fr 1fr;
                    padding: 10px;
                    border: 1px solid var(--neutrals-grey-06);
                    font-family: MaerskText;
                    font-size: 12px;
                    color: var(--neutrals-grey-04, black);
                    height: 70px;
                }

                .port-name {
                    color: var(--primary-blue-04, blue);
                    font-size: 14px;
                    text-transform: uppercase;
                    font-weight: bold;
                }

                .port-details {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                }

                .port-date {
                    display: grid;
                    grid-template-rows: auto auto;
                }

                .timeHeader {
                    font-size: 12px;
                    color: var(--neutrals-grey-02, black);
                }

                .time {
                    font-size: 14px;
                    color: var(--neutrals-grey-04, grey);
                }

                .actions {
                    grid-row: 1 / span 2;
                    display: grid;
                    place-items: center;
                    grid-template-columns: 1fr 1fr;
                }

                .action-button {
                    place-items: center;
                    height: 31px;
                    width: 139px;
                    background: var(--neutrals-grey-10, white);
                    border-radius: 2.5px;
                    font-size: 14px;
                    color: var(--neutrals-grey-02, gray);
                    cursor: pointer;
                }

                .action-button > svg {
                    width: 11px;
                    height: 12px;
                    fill: currentColor;
                    margin-right: 3px;
                }

                .action-button:hover {
                    background: var(--neutrals-grey-09, gray);
                }

                .action-button.change {
                    color: var(--neutrals-grey-02, gray);
                    border: 1px solid var(--neutrals-grey-04, gray);
                }

                .action-button.omit {
                    color: var(--schedule-edit-omit-port, red);
                    border: 1px solid var(--schedule-edit-omit-port, red);
                }

                .add {
                    display: none;
                }

                .add > svg {
                    width: 10px;
                    height: 10px;
                    fill: currentColor;
                    margin-right: 3px;
                }

                .addport:hover .add {
                    color: var(--primary-blue-05);
                    background: rgba(var(--primary-blue-05-rgb),0.2);
                    display: grid;
                    place-content: center;
                    grid-template-columns: auto auto;
                    width: 100%;
                    height: 16px;
                    font-size: 10px;
                    border: none;
                    cursor: pointer;
                }

                .addport {
                    height: 16px;
                    width: 100%;
                }

                .options {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    padding: 12.5px;
                }

                .save {
                    color: white;
                    fill: currentColor;
                    grid-column: 2;
                    height: 32px;
                    border-radius: 2.5px;
                    background-color: var(--primary-blue-05);
                    cursor: pointer;
                }

                .save > svg {
                    height: 14px;
                }
        `];
    }

    render() {
        return html`
            <div class="container">
                <div class="header">Below you can see your vessel’s schedule. You can make changes to it by placing the mouse cursor on a port or between two ports.</div>
                <div class="ports" id="ports-list">
                    ${this._populate()}
                </div>
                <div class="options">
                    <button class="save" @click=${() => this._saveAndClose()}>${SvgIcons.solidSave} Save & Exit</button>
                </div>
                ${this._showAddPort
                    ? html`<vessel-edit-schedule-add
                                @cancel=${() => this._showAddPort = false }
                                ?shown=${this._showAddPort} .arrivalPort=${this._userModifiedPort}
                                .departurePort=${this._addDeparturePort}
                                @accept=${this._onAddCompleted}>
                            </vessel-edit-schedule-add>`
                    : html``}
                ${this._showModifyPort 
                    ? html `<vessel-edit-schedule-modify
                                @cancel=${() => this._showModifyPort = false }
                                ?shown=${this._showModifyPort} .resultingPort=${this._userModifiedPort}
                                .previousPort=${this._currentPorts[this._currentlyEditedPortcallIndex-1]}
                                .currentPort=${this._currentPorts[this._currentlyEditedPortcallIndex]}
                                .nextPort=${this._currentPorts[this._currentlyEditedPortcallIndex+1]}
                                @accept=${this._onModifyCompleted}>
                            </vessel-edit-schedule-modify>`
                    : html``}
                ${this._showWarning 
                    ? html `<vessel-edit-schedule-warning
                                @cancel=${() => this._cancelWarningAction!() }
                                ?shown=${this._showWarning} .userModifiedPort=${this._userModifiedPort}
                                .conflictPort=${this._warningConflictPort}>
                            </vessel-edit-schedule-warning>`
                    : html``}
            </div>
       `;
    }

    connectedCallback() {
        super.connectedCallback();
        VoyageSchedulesModel!.addEventListener('schedule-states-updated', this._fetchSchedule);
        this._fetchSchedule();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyageSchedulesModel!.removeEventListener('schedule-states-updated', this._fetchSchedule);
    }

    @property({attribute: false}) private _currentPorts: SimplifiedPortCall[] = [];
    @property({attribute: false}) private _hovering: number = -1;
    @property({attribute: false}) private _currentlyEditedPortcallIndex: number = -1;
    @property({attribute: false}) private _showAddPort: boolean = false;
    @property({attribute: false}) private _showModifyPort: boolean = false;
    @property({attribute: false}) private _showWarning: boolean = false;

    private _addDeparturePort: SimplifiedPortCall | null = null;
    private _userModifiedPort: SimplifiedPortCall | null = null;
    private _warningConflictPort: SimplifiedPortCall | null = null;
    private _cancelWarningAction: (() => void) | null = null;
    private _firstCurrentPortIndex: number = 0;

    private async _fetchSchedule() {
        const schedule = deepCopy(await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!) || []);
        
        this._schedule = schedule;
        this._currentPorts = this._schedule.filter(s => !s.omitted).map(s => s.arrivalPortCall!.current);
        this._firstCurrentPortIndex = this._currentPorts.findIndex(port => new Date(port.arrivalAt).getTime() > Date.now()); // find the first port that is not in the past
        if (this._firstCurrentPortIndex === -1)
        {
            // No future port calls
            this._firstCurrentPortIndex = this._currentPorts.length;
        }

        this.requestUpdate();
    }

    private _renderRow(port: SimplifiedPortCall, idx: number) {
        const terminal = TerminalsModel!.getTerminal(port.terminalCode);
        let arrivalTime;
        let departureTime;
        if (terminal && !TerminalsModel!.isPseudoTerminal(port.terminalCode))
        {
            arrivalTime = GetFormattedDate(ConvertDateToAnotherTimeZone(port.arrivalAt, terminal.olsonTimezone!));
            departureTime = GetFormattedDate(ConvertDateToAnotherTimeZone(port.departureAt, terminal.olsonTimezone!));
        }
        else
        {
            arrivalTime = GetFormattedDate(port.arrivalAt);
            departureTime = GetFormattedDate(port.departureAt);
        }

        return html`
            <div class="port" id=${idx} @mouseenter=${() => { this._hovering = idx; } } @mouseleave=${() => { this._hovering = -1; }}>
                ${ this._hovering === idx ? html`
                <div class="actions">
                    <button class="action-button change" @click=${() => this._modify(idx)}>${SvgIcons.retweet} Change port</button>
                    <button class="action-button omit" @click=${() => this._omit(idx)}>${SvgIcons.trashAlt} Omit port</button>
                </div>    
                ` : html`
                <div class="port-name">${terminal && terminal.city ? terminal.city : port.terminalCode}</div>
                <div class="port-details">
                    <div class="port-date">
                        <div class="timeHeader">Arrival</div>
                        <div class="time" data-test="arrivalTime">${arrivalTime}</div>
                    </div>
                    <div class="port-date">
                        <div class="timeHeader">Departure</div>
                        <div class="time" data-test="departureTime">${departureTime}</div>
                    </div>
                </div>
                ` }
            </div>

            <div class="addport">
                <button class="add" @click=${() => this._add(idx)}>${SvgIcons.plusSquare} ADD PORT INDUCEMENT</button>
            </div>
        `;
    }

    private _populate() {
        // don't allow to edit schedule entry that is in the past and don't show entries where the voyage leg beyond 12 weeks in the future
        let cutoffIdx = this._currentPorts.findIndex(port => new Date(port.departureAt).getTime() > (Date.now() + ONE_MONTH * 3));
        const ports = this._currentPorts.slice(this._firstCurrentPortIndex, cutoffIdx < 0 ? this._currentPorts.length : cutoffIdx + 1 );

        return html`
            <div class="addport">
                <button class="add" @click=${() => this._add(this._firstCurrentPortIndex-1)}>${SvgIcons.plusSquare} ADD PORT INDUCEMENT</button>
            </div>

            ${ports.map((port, index) => html`${this._renderRow(port, index + this._firstCurrentPortIndex)}`)}
        `;
    }

    private _saveAndClose() {
        this.dispatchEvent(new Event(`close-editor`));
    }

    private _add(id: number) {
        this._userModifiedPort = null;
        this._showAddPort = true;
        this._addDeparturePort = id < 0 ? null : this._currentPorts[id];
        this.requestUpdate();
    }

    private _verifyPortOverlap(originalPort: SimplifiedPortCall, modifiedPort: SimplifiedPortCall, shift: number) {
        let conflicPort: SimplifiedPortCall | null = null;
        const index = this._currentPorts.findIndex(p => p === originalPort);

        // next port
        if (index+1 < this._currentPorts.length // if its the last entry allow any value (can't check beyond what is in the schedule)
            && (new Date(this._currentPorts[index+1].arrivalAt) < new Date(modifiedPort.arrivalAt) || new Date(this._currentPorts[index+1].arrivalAt) < new Date(modifiedPort.departureAt))) {
            conflicPort = this._currentPorts[index+1];
        }

        // previous port
        if (index+shift >= 0 
            && ((new Date(this._currentPorts[index+shift].departureAt) > new Date(modifiedPort.arrivalAt) || new Date(this._currentPorts[index+shift].departureAt) > new Date(modifiedPort.departureAt)))) {
            conflicPort = this._currentPorts[index+shift];
        }

        return conflicPort;
    }

    private async _onAddCompleted(evt: CustomEvent<SimplifiedPortCall>) {
        const newPort = evt.detail;
        this._warningConflictPort = this._verifyPortOverlap(this._addDeparturePort!, newPort, 0);

        if (this._warningConflictPort) {
            this._userModifiedPort = newPort;
            this._showWarning = true;
            this._cancelWarningAction = () => { this._showAddPort = true; this._showWarning = false; }
        } else {
            await VoyageSchedulesModel!.addPortCall(newPort);
            this._fetchSchedule();
            this._addDeparturePort = null;
        }

        this._showAddPort = false;
    }

    private _modify(id: number) {
        this._userModifiedPort = null;
        this._showModifyPort = true;
        this._currentlyEditedPortcallIndex = id;

        this.requestUpdate();
    }

    private async _onModifyCompleted(evt: CustomEvent<SimplifiedPortCall>) {
        const modifiedPort = evt.detail;
        
        await VoyageSchedulesModel!.changePortCall(modifiedPort);
        this._fetchSchedule();
        this._showModifyPort = false;
    }

    private async _omit(id: number) {
        const dialog = VoyageDialogPopupManager.createDialog({
            headerText: "Omit port",
            bodyText: "Are you sure you want to omit this port from the vessel’s schedule?",
            acceptButtonText: "Yes, Omit",
            rejectButtonText: "No, Cancel"
        });

        const response = await dialog.getResponse();
        if (response === "accept")
        {
            await VoyageSchedulesModel!.deletePortCall(this._currentPorts[id]);
            this._fetchSchedule();
        }
    }
}
